import { apiEndpoint } from '@/environment'
import {
  getPortfolioUpdatedAtFromLocalStorage,
  setPortfolioToLocalStorage,
} from '@/localStorage'
import type { Portfolio } from '@/types'
import { compress } from '@/utils'
import { getDatabaseFile } from '@/utils/database'
import { AuthTokenExpiredException } from '@pkg/basic'

export const syncDbFileToRemote = async (params: {
  portfolio: Portfolio
  getToken: () => Promise<string | null>
}) => {
  const { portfolio, getToken } = params

  const localUpdatedAt = getPortfolioUpdatedAtFromLocalStorage() ?? 0
  if (localUpdatedAt <= portfolio.updatedAt) {
    return
  }

  // DB file を取得
  const file = await getDatabaseFile(portfolio.uuid)
  // 圧縮 (gzip)
  const started = Date.now()
  const gzipFile = await compress(file)
  console.log(`${Date.now() - started}ms: DB file compressed`)

  if (process.env.NODE_ENV === 'development') {
    // 開発環境では upload API endpoint を使用
    const body = new FormData()
    body.append('file', gzipFile)
    const url = new URL(
      `/v1/portfolio/${portfolio.uuid}/db?timestamp=${localUpdatedAt}`,
      apiEndpoint,
    )
    const token = await getToken()
    if (token === null) {
      throw new AuthTokenExpiredException()
    }
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body,
    })
    if (!response.ok) {
      throw new Error(
        `Failed to upload portfolio DB file: ${response.status} ${response.statusText}`,
      )
    }
    setPortfolioToLocalStorage({
      ...portfolio,
      updatedAt: localUpdatedAt,
    })
    return
  }

  // 本番環境では presign URL を発行して upload
  const tokenForPresigned = await getToken()
  if (tokenForPresigned === null) {
    throw new AuthTokenExpiredException()
  }
  const presignedResponse = await fetch(
    `${apiEndpoint}/v1/portfolio/${portfolio.uuid}/db/upload/${localUpdatedAt}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenForPresigned}`,
      },
    },
  )
  if (!presignedResponse.ok) {
    throw new Error(
      `Failed to get presigned URL: ${presignedResponse.status} ${presignedResponse.statusText}`,
    )
  }
  const presignedUrl = await presignedResponse.text()
  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/gzip',
    },
    body: gzipFile,
  })
  if (!uploadResponse.ok) {
    throw new Error(
      `Failed to upload portfolio DB file: ${uploadResponse.status} ${uploadResponse.statusText}`,
    )
  }
  // upload 完了を API に通知
  const tokenForUploaded = await getToken()
  if (tokenForUploaded === null) {
    throw new AuthTokenExpiredException()
  }
  const response = await fetch(
    `${apiEndpoint}/v1/portfolio/${portfolio.uuid}/db/uploaded/${localUpdatedAt}`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenForUploaded}`,
      },
    },
  )
  if (!response.ok) {
    throw new Error(
      `Failed to notify upload completion: ${response.status} ${response.statusText}`,
    )
  }
  // ローカルのポートフォリオを更新
  setPortfolioToLocalStorage({
    ...portfolio,
    updatedAt: localUpdatedAt,
  })
}
